import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { SmartLink } from "../lib/navLink.jsx";

/**
 * section props: id, kicker, title, variant, tiles
 *
 * variant "cards" — horizontal card: image left, text right
 *   tile fields: category, title, body, linkLabel, linkHref, imageUrl, imageAlt, colMd
 *
 * variant "grid" (default) — full-bleed image tiles with hover caption overlay
 *   tile fields: caption, imageUrl, imageAlt, colMd
 */
export default function SectionCollage({ section }) {
  if (!section?.tiles?.length) return null;
  const { id, kicker, title, variant, tiles } = section;

  if (variant === "cards") {
    return (
      <section
        className="pmp-collage-section pmp-collage-section--cards"
        id={id}
      >
        <Container>
          {kicker && <p className="pmp-kicker mb-2">{kicker}</p>}
          <h2 className="h3 fw-bold mb-4">{title}</h2>
          <Row className="g-4">
            {tiles.map((tile, i) => (
              <Col key={`${tile.title ?? tile.category}-${i}`} md={tile.colMd ?? 6}>
                <SmartLink
                  href={tile.linkHref || "#"}
                  className="pmp-collage-card-link d-block text-decoration-none h-100"
                >
                  <div className="pmp-collage-card">
                    {/* Image column */}
                    <div className="pmp-collage-card-img-col">
                      {tile.imageUrl ? (
                        <img
                          src={tile.imageUrl}
                          alt={tile.imageAlt ?? tile.title}
                        />
                      ) : (
                        <div className="pmp-collage-card-img-placeholder">
                          {tile.imageAlt ?? tile.category}
                        </div>
                      )}
                      {tile.category && (
                        <span className="pmp-collage-card-badge">
                          {tile.category}
                        </span>
                      )}
                    </div>

                    {/* Text column */}
                    <div className="pmp-collage-card-body">
                      <h3 className="pmp-collage-card-title">{tile.title}</h3>
                      {tile.body && (
                        <p className="pmp-collage-card-desc">{tile.body}</p>
                      )}
                      <span className="pmp-collage-card-arrow">
                        {tile.linkLabel ? `${tile.linkLabel} ` : ""}&#x2192;
                      </span>
                    </div>
                  </div>
                </SmartLink>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    );
  }

  // Default: full-bleed image grid with hover-reveal caption overlay
  return (
    <section className="pmp-collage-section" id={id}>
      <Container>
        {kicker && <p className="pmp-kicker mb-2">{kicker}</p>}
        <h2 className="h3 fw-bold mb-4">{title}</h2>
        <Row className="g-3">
          {tiles.map((tile, i) => (
            <Col key={`${tile.caption}-${i}`} md={tile.colMd ?? 6}>
              <figure className="mb-0 h-100">
                <div className="pmp-collage-tile-wrap position-relative rounded overflow-hidden">
                  <div className="ratio ratio-4x3 pmp-placeholder-img">
                    {tile.imageUrl ? (
                      <img
                        src={tile.imageUrl}
                        alt={tile.imageAlt ?? tile.caption}
                        className="w-100 h-100 object-fit-cover"
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center text-secondary small px-3 text-center">
                        {tile.imageAlt ?? tile.caption}
                      </div>
                    )}
                  </div>
                  <div className="pmp-collage-overlay" aria-hidden="true">
                    <span className="pmp-collage-caption-text">
                      {tile.caption}
                    </span>
                  </div>
                </div>
                <figcaption className="mt-2 small fw-semibold text-secondary">
                  {tile.caption}
                </figcaption>
              </figure>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
}
